import { keyframes, styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { EvidenceMass } from "../../../../types/claims";

interface VerdictBalanceProps {
  /** Summed source weight on each side of the verdict. */
  mass: EvidenceMass;
}

/** Share of a half filled by one side, with the total evidence mass as the full scale. */
function toFill(side: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(1, side / total)) * 100;
}

const slideFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BalanceRoot = styled("div")({
  animation: `${slideFadeIn} 400ms ease-out`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

const BalanceTrack = styled("div")({
  display: "flex",
  height: 4,
});

const BalanceHalf = styled("div")<{ $side: "contradicting" | "supporting" }>(
  ({ theme, $side }) => ({
    position: "relative",
    flex: 1,
    borderRadius: $side === "contradicting" ? "2px 0 0 2px" : "0 2px 2px 0",
    backgroundColor: theme.palette.action.hover,
  })
);

const BalanceFill = styled("div")<{ $side: "contradicting" | "supporting"; $width: number }>(
  ({ theme, $side, $width }) => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    [$side === "contradicting" ? "right" : "left"]: 0,
    width: `${$width}%`,
    borderRadius: "inherit",
    backgroundColor: theme.verdicts[$side === "contradicting" ? "CONTRADICTED" : "SUPPORTED"].main,
    opacity: 0.7,
  })
);

const BalanceCenterLine = styled("div")(({ theme }) => ({
  width: 2,
  margin: "-2px 2px",
  backgroundColor: theme.palette.text.disabled,
}));

/**
 * A diverging bar of the weight behind each side of the verdict: contradicting grows left,
 * supporting grows right. Each side is scaled to the total evidence mass, so the bar shows the
 * split between the sides. It carries no legend: it sits inside VerdictScale, whose end labels
 * name both sides. Meant for claims whose evidence disagrees; the caller decides when that is.
 */
export function VerdictBalance({ mass }: VerdictBalanceProps) {
  const { t } = useTranslation();

  return (
    <BalanceRoot
      role="img"
      aria-label={t("verdictBalance.ariaLabel")}
      data-testid="verdict-balance"
    >
      <BalanceTrack>
        <BalanceHalf $side="contradicting">
          <BalanceFill $side="contradicting" $width={toFill(mass.contradict, mass.total)} />
        </BalanceHalf>
        <BalanceCenterLine />
        <BalanceHalf $side="supporting">
          <BalanceFill $side="supporting" $width={toFill(mass.support, mass.total)} />
        </BalanceHalf>
      </BalanceTrack>
    </BalanceRoot>
  );
}
